import { useEffect, useState } from "react";
import { Sparkles, X, Download, Share } from "lucide-react";
import { isStandalone, isIOS } from "@/lib/push";

const DISMISS_KEY = "le-salon-install-dismissed";

export const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari doesn't support beforeinstallprompt — show manual instructions
    if (isIOS() && !window.navigator.standalone) {
      const t = setTimeout(() => setShow(true), 5000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (isIOS()) {
      setIosHint(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      dismiss();
    }
    setDeferredPrompt(null);
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-banner"
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        maxWidth: 480,
        width: "calc(100% - 32px)",
        background: "var(--surface)",
        border: "1px solid var(--border-default)",
        boxShadow: "0 12px 40px rgba(28,25,23,0.18)",
        padding: 24,
        zIndex: 100,
      }}
    >
      <button
        onClick={dismiss}
        data-testid="install-dismiss"
        aria-label="Dismiss install prompt"
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          background: "transparent",
          border: "none",
          color: "var(--text-muted)",
          cursor: "pointer",
          padding: 4,
        }}
      >
        <X size={16} strokeWidth={1.5} />
      </button>

      <div
        className="overline"
        style={{
          color: "var(--brand-accent)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Sparkles size={12} strokeWidth={1.4} /> Bring Le Salon home with you
      </div>

      {!iosHint ? (
        <>
          <p
            className="font-serif"
            style={{
              fontSize: 18,
              fontStyle: "italic",
              color: "var(--text-primary)",
              margin: "0 0 12px",
              lineHeight: 1.45,
            }}
          >
            Add Le Salon to your home screen — a private little doorway, always one tap away.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            <button
              onClick={install}
              data-testid="install-confirm"
              className="btn-accent"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Download size={14} strokeWidth={1.5} /> {isIOS() ? "Show me how" : "Add to home screen"}
            </button>
            <a
              href="/install"
              data-testid="install-learn-more"
              className="btn-secondary"
              style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}
            >
              How does it work?
            </a>
            <button onClick={dismiss} data-testid="install-later" style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 13, padding: "8px 4px" }}>
              Not now
            </button>
          </div>
        </>
      ) : (
        <>
          <p
            className="font-serif"
            style={{
              fontSize: 15,
              color: "var(--text-primary)",
              margin: "0 0 12px",
              lineHeight: 1.6,
            }}
          >
            <strong>To install on iPhone, follow these three steps in Safari:</strong>
            <br />
            <br />
            <strong>1.</strong> Tap the <Share size={14} style={{ display: "inline", verticalAlign: "text-bottom" }} strokeWidth={1.5} /> <strong>Share icon</strong> at the bottom of your Safari screen (a square with an ↑ arrow).
            <br />
            <br />
            <strong>2.</strong> Scroll down and tap <strong>"Add to Home Screen"</strong>.
            <br />
            <br />
            <strong>3.</strong> Tap <strong>"Add"</strong> in the top-right corner. Le Salon will appear on your home screen.
            <br />
            <br />
            <em style={{ color: "var(--text-muted)", fontSize: 13 }}>
              (This banner is just instructions — Apple doesn't allow websites to install themselves. Only you can, through Safari.)
            </em>
          </p>
          <button onClick={dismiss} className="btn-secondary" data-testid="install-ios-ok" style={{ marginTop: 8 }}>
            Close instructions
          </button>
        </>
      )}
    </div>
  );
};
